import { Injectable } from '@angular/core';
import { generateIdentity, getIdentity, IDENTITY_KEY, Metamodel, Named, setIdentity, UUID } from 'shell-models-lib';
import { IIdentifierProvider } from 'app/model/services/identifier/IIdentifierProvider';
import { AzureIdentifierProvider } from 'app/model/services/identifier/azure-identifier-provider';
import { TridentIdentifierProvider } from 'app/model/services/identifier/trident-identifier-provider';
import { IResourceBase } from 'app/shared/utils/resourcefiles.utils';

@Injectable({
    providedIn: 'root'
})
export class IdentityService {
    private readonly _identifierProvider: IIdentifierProvider;

    constructor() {
        this._identifierProvider = __IS_FABRIC_DPE__ || __IS_FABRIC_MDF__
            ? new TridentIdentifierProvider()
            : new AzureIdentifierProvider();
    }

    public generateIdentifierFromResource(resource: IResourceBase): string {
        return this._identifierProvider.generateIdentifierFromResource(resource);
    }

    public getUniqueKeyFromNamed(named: Named): string {
        return this._identifierProvider.getUniqueKeyFromNamed(named);
    }

    public getUniqueKeyFromResource(resource: Partial<Record<'name' | 'objectId', string>>): string {
        return this._identifierProvider.getUniqueKeyFromResource(resource);
    }

    public generateResource(type: string, uniqueKey: string): IResourceBase {
        return this._identifierProvider.generateResource(type, uniqueKey);
    }

    public getResourceIdentifier(type: string, uniqueKey: string): string {
        return generateIdentity(type, uniqueKey);
    }

    /*
        Identifies a named and returns the identity used to identify
    */
    public identify(named: Named): string {
        if (!named.extension[IDENTITY_KEY]) {
            const model = Metamodel.modelOf(named);
            // for non-root nameds fall back to uuid (for safety)
            named.extension[IDENTITY_KEY] =
                model && model.concept && model.concept.name && this.getUniqueKeyFromNamed(named)
                    ? this.getResourceIdentifier(model.concept.name, this.getUniqueKeyFromNamed(named))
                    : UUID.uuid4();
        }
        return named.extension[IDENTITY_KEY] as string;
    }

    /*
        Re-identifies a named and returns the new identity
    */
    public reIdentify(named: Named): string {
        this.anonymize(named);
        return this.identify(named);
    }

    public anonymize(named: Named): void {
        if (named?.extension) {
            Reflect.deleteProperty(named.extension, IDENTITY_KEY);
        }
    }

    /*
        Identifies a named and returns the identity used to identify
    */
    public setIdentity(named: Named, name: string, forceSet?: boolean): string {
        return setIdentity(named, name, forceSet);
    }

    /*
        Returns the identity
    */
    public identity(named: Named): string | undefined {
        return getIdentity(named);
    }
}
